'use client';

import React, { useEffect, useRef } from 'react';

type TrustpilotApi = {
  loadFromElement: (element: HTMLElement, forceReload?: boolean) => void;
};

declare global {
  // eslint-disable-next-line no-var, vars-on-top
  var Trustpilot: TrustpilotApi | undefined;
}

interface TrustpilotEmbedProps {
  html: string;
}

/**
 * Renders a pasted Trustpilot embed reliably in the App Router SPA.
 *
 * The markup is injected imperatively into a ref'd, otherwise-empty div, so React only manages
 * the wrapper and never reconciles the children — it therefore cannot wipe the <iframe> Trustpilot
 * injects. That's the "shows then disappears" bug you get from `dangerouslySetInnerHTML`, where
 * React reclaims the div on hydration/re-render. `loadFromElement` runs on mount so the widget
 * also renders when the page is reached via client-side navigation (Trustpilot's bootstrap script
 * only auto-scans once, on load).
 */
export function TrustpilotEmbed({ html }: Readonly<TrustpilotEmbedProps>) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    container.innerHTML = html;

    const widget = container.querySelector<HTMLElement>('.trustpilot-widget');
    if (widget && globalThis.Trustpilot) {
      globalThis.Trustpilot.loadFromElement(widget, true);
    }
  }, [html]);

  return <div ref={containerRef} />;
}
